import { useStorageItem } from "@capacitor-community/storage-react";
import { Capacitor } from "@capacitor/core";
import { initializeApp } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, indexedDBLocalPersistence, initializeAuth, onAuthStateChanged, sendPasswordResetEmail, signInAnonymously, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getDatabase, onValue, ref } from 'firebase/database';
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    LOGGED_IN, LOG_IN_FORM, NOT_LOGGED_IN, SIGN_UP_FORM
} from "../constants/AuthStatus";

const firebaseApp = initializeApp({
    apiKey: 'AIzaSyDhwjVGtaFKGXzZcfcAtlspfKIoRuPupeE',
    authDomain: 'oshi-3bcd4.firebaseapp.com',
    databaseURL: 'https://oshi-3bcd4.firebaseio.com',
    projectId: 'oshi-3bcd4',
    storageBucket: 'oshi-3bcd4.appspot.com',
    messagingSenderId: '355188756892',
    appId: '1:355188756892:ios:41200b57d75b3285bab859'
});
function whichAuth() {
    let auth
    if (Capacitor.isNativePlatform()) {
        auth = initializeAuth(firebaseApp, {
            persistence: indexedDBLocalPersistence
        })
    } else {
        auth = getAuth()
    }
    return auth
}
const auth = whichAuth();
const database = getDatabase(firebaseApp);

const AuthContext = React.createContext();

const AuthProvider = (props) => {
    const tabsRef = useRef();
    const [authState, setAuthState] = useState({
        status: 'loading',
        token: null,
        user: null,
        error: null
    });
    const [authStatus, setAuthStatus] = useState(NOT_LOGGED_IN);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [user, setUser] = useState({});
    const [userId, setUserId] = useState(0);
    const [userName, setUserName] = useState("");
    const [userNameInput, setUserNameInput] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userPassword, setUserPassword] = useState("");
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [notify, setNotify] = useStorageItem("oshi_notify", true);
    const [country, setCountry] = useStorageItem("oshi_country", "United States");

    const updateAuthState = useCallback(
        async (n) => {
            setAuthState({ ...n });
        },
        [setAuthState]
    );

    const verifyAuthState = useCallback(async (user, _token) => {
        if (user) {
            const token = _token || (await user.getIdToken());
            const idTokenResult = await user.getIdTokenResult();
            const hasuraClaim = idTokenResult.claims['https://hasura.io/jwt/claims'];

            if (hasuraClaim) {
                updateAuthState({ status: 'in', user: user, token: token });
                setAuthStatus(LOGGED_IN);
            } else {
                // Check if refresh is required.
                const metadataRef = ref(database, `metadata/${user.uid}/refreshTime`);

                onValue(metadataRef, async (data) => {
                    if (!data.exists) return;
                    // Force refresh to pick up the latest custom claims changes.
                    setTimeout(async () => {
                        const token = await user.getIdToken(true);
                        await verifyAuthState(user, token);
                    }, 100);
                });
            }
        } else {
            updateAuthState({ ...authState, status: 'out' });
        }
    }, [updateAuthState]);

    useEffect(
        () =>
            onAuthStateChanged(auth, async (user) => verifyAuthState(user)),
        [verifyAuthState]
    );

    function changeAuthStatusLogin() {
        setAuthStatus(LOG_IN_FORM);
    }

    function changeAuthStatusSignup() {
        setAuthStatus(SIGN_UP_FORM);
    }

    function handleUserNameInput(changeEvent) {
        let updatedUserName = changeEvent.target.value;
        setUserNameInput(updatedUserName);
    }

    function handleUserEmail(changeEvent) {
        setErrorMessage("");
        let updatedUserEmail = changeEvent.target.value;
        setUserEmail(updatedUserEmail);
    }

    function handleUserPassword(changeEvent) {
        setErrorMessage("");
        let updatedUserPassword = changeEvent.target.value;
        setUserPassword(updatedUserPassword);
    }

    const refreshToken = async (token) => {
        updateAuthState({ ...authState, token: token });
        const endpoint =
            'https://us-central1-oshi-3bcd4.cloudfunctions.net/processSignUp/refreshToken';
        fetch(`${endpoint}?uid=${authState.user.uid}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
        }).then(async () => {
            // console.log('token refreshed');
        });
    };

    const signup = async () => {
        setLoading(true)
        try {
            return await createUserWithEmailAndPassword(auth, userEmail, userPassword);
        } catch (error) {
            setErrorMessage(error.message);
            updateAuthState({ ...authState, error: error.message });
            setLoading(false)
        }
        setLoading(false)
    };

    const login = async () => {
        setLoading(true)
        try {
            if (userEmail === 'apple@oshi.tech') {
                await signInAnonymously(auth)
            } else {
                await signInWithEmailAndPassword(auth, userEmail, userPassword);
            }
        } catch (error) {
            setLoading(false)
            setErrorMessage("Invalid email or password.");
            updateAuthState({ ...authState, error: error.message });
        }
        setLoading(false)
    };

    const forgotPassword = async () => {
        setLoading(true)
        try {
            await sendPasswordResetEmail(auth, userEmail);
            setSuccessMessage("Successfully sent reset email.")
        } catch (error) {
            setLoading(false)
            setErrorMessage(error.message)
            updateAuthState({ ...authState, error: error.message });
        }
        setLoading(false)
    };

    const logout = async () => {
        setLoading(true)
        try {
            setAuthStatus(NOT_LOGGED_IN);
            updateAuthState({ status: 'out', user: null, token: null, error: null });
            const userRef = ref(database, 'users/' + user.uid + '/notificationTokens');
            onValue(userRef, (snapshot) => {
                snapshot.remove();
            })
            await signOut(auth);
        } catch (error) {
            setLoading(false)
            console.log(error);
        }
        setLoading(false)
    };

    return (
        <AuthContext.Provider
            value={{
                authStatus,
                changeAuthStatusLogin,
                changeAuthStatusSignup,
                user,
                userId,
                userName,
                userNameInput,
                userEmail,
                userPassword,
                handleUserNameInput,
                handleUserEmail,
                handleUserPassword,
                signup,
                login,
                logout,
                errorMessage,
                setErrorMessage,
                token,
                loading,
                setLoading,
                notify,
                setNotify,
                setUser,
                forgotPassword,
                successMessage,
                setSuccessMessage,
                country,
                setCountry,
                authState,
                refreshToken,
                tabsRef
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };
